/**
 * The GHL editor occasionally serialises a link object into an href, shipping
 * <a href="[object Object]"></a> (found by the dash site-graph crawler 2026-07-30).
 * Sanitise every CMS-sourced body at ingest: empty broken anchors are dropped,
 * non-empty ones unwrap onto their text.
 */
export const sanitizeCmsHtml = (html?: string) =>
  html
    ?.replace(/<a\b[^>]*href="\[object[^"]*"[^>]*>\s*<\/a>/gi, "")
    .replace(/<a\b[^>]*href="\[object[^"]*"[^>]*>([\s\S]*?)<\/a>/gi, "$1");

// Shared RSS parsing for section feeds. Used by the Updates changelog index;
// Blog.tsx keeps its own inline parser to avoid touching the live blog.

export type RssItem = {
  id: string;
  title: string;
  link: string;
  slug: string;
  pubDate: string;
  formattedDate: string;
  description: string;
  content?: string;
  coverImage?: string;
  categories: string[];
};

export const formatDate = (s: string) => {
  const d = new Date(s);
  return Number.isNaN(d.getTime())
    ? ""
    : d.toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" });
};

const stripHtml = (html: string) => {
  if (!html) return "";
  const doc = new DOMParser().parseFromString(html, "text/html");
  return doc.body.textContent?.replace(/\s+/g, " ").trim() ?? "";
};

export const parseRssItems = (raw: string, limit = 50): RssItem[] => {
  const xml = new DOMParser().parseFromString(raw, "application/xml");
  if (xml.querySelector("parsererror")) throw new Error("Unable to parse RSS feed");

  return Array.from(xml.querySelectorAll("item"))
    .slice(0, limit)
    .map((item, index) => {
      const title = item.querySelector("title")?.textContent?.trim() ?? "Untitled";
      const link = item.querySelector("link")?.textContent?.trim() ?? "";
      const pubDate = item.querySelector("pubDate")?.textContent?.trim() ?? "";
      const description = item.querySelector("description")?.textContent ?? "";
      const content = sanitizeCmsHtml(item.getElementsByTagName("content:encoded")?.[0]?.textContent ?? undefined);
      const coverImage =
        item.getElementsByTagName("media:content")?.[0]?.getAttribute("url") ||
        item.getElementsByTagName("media:content")?.[0]?.textContent ||
        undefined;
      const categories = Array.from(item.querySelectorAll("category"))
        .map((c) => c.textContent?.trim() ?? "")
        .filter(Boolean);

      let slug = "";
      try {
        slug = new URL(link).pathname.split("/").filter(Boolean).pop() ?? "";
      } catch {
        slug = link.split("/").filter(Boolean).pop() ?? `${index}`;
      }

      return {
        id: item.querySelector("guid")?.textContent?.trim() ?? `${index}-${title}`,
        title,
        link,
        slug,
        pubDate,
        formattedDate: formatDate(pubDate),
        description: stripHtml(description),
        content,
        coverImage: coverImage ?? undefined,
        categories,
      };
    })
    .filter((i) => i.link && i.slug);
};

// Load a section's feed: prerendered local file first, GHL external as fallback.
export const loadSectionFeed = async (
  localFeed: string,
  externalFeed: string,
  limit = 50,
): Promise<RssItem[]> => {
  let response = await fetch(localFeed);
  if (!response.ok) {
    response = await fetch(externalFeed, {
      headers: { Accept: "application/rss+xml, application/xml;q=0.9, */*;q=0.8" },
    });
  }
  if (!response.ok) throw new Error(`Feed returned ${response.status}`);
  return parseRssItems(await response.text(), limit);
};
